import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './InventoryMgmtPage.css';

function urgencyClass(row) {
  const d = row.DaysRemaining;
  if (d === null || d === undefined) return '';
  if (d < 30) return 'urg-critical';
  if (d < 90) return 'urg-warning';
  return 'urg-ok';
}

function ResultTable({ result, onDownload }) {
  return (
    <div className="im-results">
      <div className="im-results-header">
        <span className="im-results-meta">
          As of <strong>{result.as_of}</strong> · {result.total_skus} SKUs · {result.time_window_days}-day window
        </span>
        <button className="im-btn-secondary sm" onClick={onDownload}>⬇ CSV</button>
      </div>
      <div className="im-table-wrap">
        <table className="im-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th className="num">Stock</th>
              <th className="num">Net Sold 180d</th>
              <th className="num">Units/Day</th>
              <th className="num">Days Left</th>
              <th>Run Out</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row, i) => {
              const cls = urgencyClass(row);
              return (
                <tr key={i} className={cls}>
                  <td className="im-product">{row['Product title']}</td>
                  <td className="im-sku">{row.SKU}</td>
                  <td className="num">{row.Stock?.toLocaleString()}</td>
                  <td className="num">{row.NetSold?.toLocaleString()}</td>
                  <td className="num">{row.DailySalesRate}</td>
                  <td className={`num days-cell ${cls}`}>
                    {row.DaysRemaining !== null && row.DaysRemaining !== undefined
                      ? Math.round(row.DaysRemaining)
                      : <span className="im-inf">∞</span>}
                  </td>
                  <td className={`runout ${cls}`}>{row.RunOutDate}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function InventoryMgmtPage() {
  const [jobId,        setJobId]        = useState(null);
  const [jobLogs,      setJobLogs]      = useState([]);
  const [jobStatus,    setJobStatus]    = useState(null);
  const [logsOpen,     setLogsOpen]     = useState(true);
  const [result,       setResult]       = useState(null);
  const [savedResults, setSavedResults] = useState([]);
  const [error,        setError]        = useState(null);
  const [loading,      setLoading]      = useState(false);
  const logViewerRef = useRef();

  function loadSavedResults() {
    axios.get('/api/inventory-mgmt/results')
      .then(r => setSavedResults(r.data))
      .catch(() => {});
  }

  useEffect(() => { loadSavedResults(); }, []);

  // Poll logs while job is active
  useEffect(() => {
    if (!jobId || jobStatus === 'complete' || jobStatus === 'error') return;
    const iv = setInterval(() => {
      axios.get(`/api/inventory-mgmt/logs/${jobId}`)
        .then(r => {
          setJobLogs(r.data.lines);
          setJobStatus(r.data.status);
          if (r.data.status === 'complete' && r.data.result) {
            setResult(r.data.result);
            loadSavedResults();
          } else if (r.data.status === 'error') {
            setError(r.data.error || 'Job failed');
          }
        })
        .catch(() => {});
    }, 2000);
    return () => clearInterval(iv);
  }, [jobId, jobStatus]);

  // Auto-scroll log viewer without yanking the page
  useEffect(() => {
    if (!logsOpen || !logViewerRef.current) return;
    const el = logViewerRef.current;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [jobLogs, logsOpen]);

  function handleRun() {
    setLoading(true);
    setError(null);
    axios.post('/api/inventory-mgmt/run')
      .then(r => {
        setJobId(r.data.job_id);
        setJobLogs([]);
        setJobStatus('pending');
        setLogsOpen(true);
        setLoading(false);
      })
      .catch(e => {
        setError(e.response?.data?.error || e.message);
        setLoading(false);
      });
  }

  function loadSavedResult(name) {
    axios.get(`/api/inventory-mgmt/results/${name}`)
      .then(r => setResult(r.data))
      .catch(() => {});
  }

  function downloadCsv(res) {
    const r = res || result;
    if (!r) return;
    const headers = ['Product Title', 'SKU', 'Stock', 'Net Sold (180d)',
                     'Units/Day', 'Days Remaining', 'Run Out Date'];
    const csvRows = r.rows.map(row => [
      `"${(row['Product title'] || '').replace(/"/g, '""')}"`,
      `"${row.SKU}"`,
      row.Stock,
      row.NetSold,
      row.DailySalesRate,
      row.DaysRemaining ?? '',
      row.RunOutDate,
    ].join(','));
    const csv  = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `inventory_${r.as_of}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  const busy = jobStatus === 'pending' || jobStatus === 'running';

  return (
    <div className="invmgmt-page">
      <h1>Inventory Management</h1>
      <p className="im-muted">
        Pulls live inventory (Shipping Pilot location) and 180-day net sales from Shopify,
        then computes days remaining per SKU.
      </p>

      {error && <div className="im-error">{error}</div>}

      {/* Actions row */}
      <div className="im-actions">
        <button className="im-btn-primary" onClick={handleRun} disabled={loading || busy}>
          {loading ? 'Starting…' : busy ? '● Running…' : '▶  Run Analysis'}
        </button>
        <Link to="/inventory-management/config" className="im-btn-secondary im-config-link">
          ⚙ Excluded SKUs
        </Link>
      </div>

      {/* Log viewer */}
      {jobId && (
        <div className="im-log-section">
          <div className="im-log-header">
            <div className="im-log-title-row">
              <span className="im-log-title">Analysis Job</span>
              {jobStatus && (
                <span className={`im-badge im-badge-${jobStatus}`}>
                  {jobStatus === 'pending'  ? 'Queued'      :
                   jobStatus === 'running'  ? '● Running'   :
                   jobStatus === 'complete' ? '✓ Complete'  :
                   jobStatus === 'error'    ? '✕ Error'     : jobStatus}
                </span>
              )}
            </div>
            <div className="im-log-controls">
              <button className="im-btn-secondary sm" onClick={() => setLogsOpen(o => !o)}>
                {logsOpen ? 'Collapse' : 'Expand'}
              </button>
              {(jobStatus === 'complete' || jobStatus === 'error') && (
                <button className="im-btn-secondary sm"
                  onClick={() => { setJobId(null); setJobLogs([]); setJobStatus(null); }}>
                  Dismiss
                </button>
              )}
            </div>
          </div>
          {logsOpen && (
            <div className="im-log-viewer" ref={logViewerRef}>
              {jobLogs.length === 0
                ? <span className="im-log-muted">Waiting for job to start…</span>
                : jobLogs.map((line, i) => <div key={i} className="im-log-line">{line}</div>)
              }
            </div>
          )}
        </div>
      )}

      {/* Current result table */}
      {result && (
        <ResultTable result={result} onDownload={() => downloadCsv(result)} />
      )}

      {/* Previous saved reports */}
      {savedResults.length > 0 && (
        <section className="im-saved-section">
          <h2 className="im-section-title">Previous Reports</h2>
          <div className="im-saved-grid">
            {savedResults.map(s => {
              const label = s.name.replace('inventory_', '').replace('.json', '');
              const isActive = result?.as_of === label;
              return (
                <button key={s.name}
                  className={`im-saved-card${isActive ? ' active' : ''}`}
                  onClick={() => loadSavedResult(s.name)}>
                  <span className="im-saved-date">{label}</span>
                  <span className="im-saved-ts">
                    {new Date(s.modified * 1000).toLocaleString()}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
